import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, ChevronRight, Search, Building, ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import { ActorTree } from '../types/actor-types';
import { cn } from '@/core/lib/utils';
import { TreeSearchBar } from './molecules/TreeSearchBar';
import { useTreeSearchStore } from '../stores/tree-search-store';
import { filterTreeBySearch, countMatches, isNodeMatch, filterTreeByEmptyLocations } from '../utils/tree-search-utils';
import { Button } from '@/core/components/ui/button';
import { Checkbox } from '@/core/components/ui/checkbox';

interface ActorTreeNode extends ActorTree {
  children: ActorTreeNode[];
  level: number;
}

interface ActorTreeViewProps {
  actors: ActorTree[];
  selectedLocationId?: string;
  onLocationSelect?: (actorId: string) => void;
  className?: string;
  shouldAutoScroll?: boolean;
}

export function ActorTreeView({
  actors,
  selectedLocationId,
  onLocationSelect,
  className = '',
  shouldAutoScroll = false
}: ActorTreeViewProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const selectedNodeRef = useRef<HTMLDivElement>(null);
  
  // Use Zustand store for search state and hide empty locations filter
  const { searchQuery, setSearchQuery, hideEmptyLocations, setHideEmptyLocations } = useTreeSearchStore();

  // Add scrollbar styles when component mounts
  useEffect(() => {
    const styleId = 'actor-tree-scrollbar-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .actor-tree-scroll {
          overflow-y: scroll !important;
          scrollbar-width: auto !important;
        }
        .actor-tree-scroll::-webkit-scrollbar {
          width: 12px !important;
          display: block !important;
        }
        .actor-tree-scroll::-webkit-scrollbar-track {
          background: #F3F4F6 !important;
          border-radius: 6px !important;
        }
        .actor-tree-scroll::-webkit-scrollbar-thumb {
          background: #9CA3AF !important;
          border-radius: 6px !important;
          border: 2px solid #F3F4F6 !important;
        }
        .actor-tree-scroll::-webkit-scrollbar-thumb:hover {
          background: #6B7280 !important;
        }
        .actor-tree-scroll::-webkit-scrollbar-corner {
          background: #F3F4F6 !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Build hierarchical tree structure
  const treeData = useMemo(() => {
    const nodeMap = new Map<number, ActorTreeNode>();
    const rootNodes: ActorTreeNode[] = [];

    // First pass: create all nodes
    actors.forEach(actor => {
      nodeMap.set(actor.actorId, {
        ...actor,
        children: [],
        level: 0
      });
    });

    // Second pass: build hierarchy
    actors.forEach(actor => {
      const node = nodeMap.get(actor.actorId)!;
      
      if (actor.parentActorId && nodeMap.has(actor.parentActorId)) {
        const parent = nodeMap.get(actor.parentActorId)!;
        parent.children.push(node);
        node.level = parent.level + 1;
      } else {
        rootNodes.push(node);
      }
    });

    // Sort nodes by actorTypeId first (27 goes last), then by name
    const sortNodes = (nodes: ActorTreeNode[]) => {
      nodes.sort((a, b) => {
        // First: Sort by actorTypeId (27 goes last)
        const aIsEVLocation = a.actorTypeId === 27;
        const bIsEVLocation = b.actorTypeId === 27;

        if (aIsEVLocation && !bIsEVLocation) return 1;  // a goes after b
        if (!aIsEVLocation && bIsEVLocation) return -1; // a goes before b

        // Second: Alphabetical by name within same type
        return (a.name || '').localeCompare(b.name || '');
      });

      // Recursively sort children
      nodes.forEach(node => sortNodes(node.children));
    };

    sortNodes(rootNodes);
    return rootNodes;
  }, [actors]);

  // Filter tree data based on search query and empty locations
  const filteredTreeData = useMemo(() => {
    // First apply search filter
    let filtered = filterTreeBySearch(treeData, searchQuery, actors);
    // Then apply empty locations filter
    filtered = filterTreeByEmptyLocations(filtered, hideEmptyLocations);
    return filtered;
  }, [treeData, searchQuery, actors, hideEmptyLocations]);

  // Count matches
  const matchCount = useMemo(() => {
    return countMatches(filteredTreeData, searchQuery);
  }, [searchQuery, filteredTreeData]);

  // Auto-expand nodes when searching
  useEffect(() => {
    if (searchQuery.trim()) {
      const nodesToExpand = new Set<number>();
      
      const collectExpandableNodes = (nodes: ActorTreeNode[]) => {
        nodes.forEach(node => {
          if (node.children.length > 0) {
            nodesToExpand.add(node.actorId);
            collectExpandableNodes(node.children);
          }
        });
      };
      
      collectExpandableNodes(filteredTreeData);
      setExpandedNodes(nodesToExpand);
    }
  }, [searchQuery, filteredTreeData]);

  // Auto-expand logic for non-search scenarios
  useEffect(() => {
    if (searchQuery.trim()) return;

    const nodesToExpand: number[] = [];

    // Always expand root nodes that have children (especially operators)
    if (treeData.length > 0) {
      treeData.forEach(rootNode => {
        if (rootNode.children.length > 0) {
          nodesToExpand.push(rootNode.actorId);
        }
      });
    }

    // Also expand path to selected location AND the selected node itself
    if (selectedLocationId && actors.length > 0) {
      const findPathToNode = (nodeId: number): number[] => {
        const actor = actors.find(a => a.actorId === nodeId);
        if (!actor) return [];

        const path = [nodeId];
        if (actor.parentActorId) {
          path.unshift(...findPathToNode(actor.parentActorId));
        }
        return path;
      };

      const selectedId = parseInt(selectedLocationId);
      const pathToSelected = findPathToNode(selectedId);
      nodesToExpand.push(...pathToSelected);

      // Also expand the selected node itself to show its children
      nodesToExpand.push(selectedId);
    }

    setExpandedNodes(new Set(nodesToExpand));
  }, [selectedLocationId, actors, treeData, searchQuery]);

  // Auto-scroll to selected location - ONLY when triggered from LocationSelector
  useEffect(() => {
    if (shouldAutoScroll && selectedLocationId && selectedNodeRef.current && scrollContainerRef.current) {
      const timeout = setTimeout(() => {
        // First expand any necessary nodes, then scroll
        const selectedElement = selectedNodeRef.current;
        const container = scrollContainerRef.current;
        if (selectedElement && container) {
          // Calculate the position to scroll to within the container
          const containerRect = container.getBoundingClientRect();
          const elementRect = selectedElement.getBoundingClientRect();
          const containerScrollTop = container.scrollTop;
          
          // Calculate the target scroll position to center the element
          const elementTop = elementRect.top - containerRect.top + containerScrollTop;
          const targetScrollTop = elementTop - (containerRect.height / 2) + (elementRect.height / 2);
          
          // Smooth scroll within the container only
          container.scrollTo({
            top: Math.max(0, targetScrollTop),
            behavior: 'smooth'
          });
          
          // Add a brief highlight effect
          selectedElement.style.transition = 'all 0.3s ease';
          selectedElement.style.transform = 'scale(1.02)';
          selectedElement.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
          
          // Remove highlight after animation
          setTimeout(() => {
            selectedElement.style.transform = '';
            selectedElement.style.boxShadow = '';
          }, 600);
        }
      }, 150); // Slightly longer delay to ensure expansion is complete

      return () => clearTimeout(timeout);
    }
  }, [selectedLocationId, shouldAutoScroll]);

  const toggleExpanded = (actorId: number) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(actorId)) {
      newExpanded.delete(actorId);
    } else {
      newExpanded.add(actorId);
    }
    setExpandedNodes(newExpanded);
  };

  // Get all expandable node IDs (nodes with children)
  const getAllExpandableNodeIds = useCallback((nodes: ActorTreeNode[]): number[] => {
    const nodeIds: number[] = [];
    nodes.forEach((node) => {
      if (node.children.length > 0) {
        nodeIds.push(node.actorId);
        nodeIds.push(...getAllExpandableNodeIds(node.children));
      }
    });
    return nodeIds;
  }, []);

  const handleExpandAll = useCallback(() => {
    const allNodeIds = getAllExpandableNodeIds(filteredTreeData);
    setExpandedNodes(new Set(allNodeIds));
  }, [filteredTreeData, getAllExpandableNodeIds]);

  const handleCollapseAll = useCallback(() => {
    setExpandedNodes(new Set());
  }, []);

  // Check if all nodes are expanded or collapsed
  const allExpandableNodeIds = useMemo(() => getAllExpandableNodeIds(filteredTreeData), [filteredTreeData, getAllExpandableNodeIds]);
  const isAllExpanded = allExpandableNodeIds.length > 0 && allExpandableNodeIds.every(id => expandedNodes.has(id));
  const isAllCollapsed = expandedNodes.size === 0;

  const handleNodeClick = (actorId: number) => {
    if (onLocationSelect) {
      onLocationSelect(actorId.toString());
    }
  };

  const renderNode = (node: ActorTreeNode, index: number = 0): React.ReactNode => {
    const isExpanded = expandedNodes.has(node.actorId);
    const isSelected = selectedLocationId === node.actorId.toString();
    const hasChildren = node.children.length > 0;
    // Consistent indentation for all nodes at the same level
    const indentLevel = node.level * 20;
    const isMatch = isNodeMatch(node, searchQuery);

    // Row hierarchy styling matching the data table
    const isTopLevelParent = node.level === 0 && hasChildren;
    const isSubGroup = node.level > 0 && hasChildren;

    const getRowClasses = () => {
      if (isSelected) {
        return 'bg-blue-50 border-l-4 border-l-blue-500';
      }
      if (isTopLevelParent) {
        return 'bg-gray-100 border-l-4 border-l-blue-500';
      }
      if (isSubGroup) {
        return 'bg-gray-50 border-l-4 border-l-blue-300';
      }
      // Zebra striping for leaf rows
      const zebraClass = index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50';
      return `${zebraClass} border-l-4 border-l-transparent`;
    };

    // Highlight matching text in the name
    const highlightMatch = (text: string) => {
      if (!searchQuery.trim() || !isMatch) {
        return text;
      }
      const regex = new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      const parts = text.split(regex);
      return parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className="bg-yellow-300 text-yellow-900 px-0.5 rounded">{part}</mark>
        ) : part
      );
    };

    return (
      <div key={node.actorId}>
        <div
          ref={isSelected ? selectedNodeRef : undefined}
          className={cn(
            "flex items-center py-2.5 px-2 cursor-pointer hover:bg-blue-50/50 transition-colors",
            getRowClasses()
          )}
          style={{ paddingLeft: `${indentLevel + 8}px` }}
          onClick={() => handleNodeClick(node.actorId)}
        >
          {hasChildren ? (
            <button
              className="flex-shrink-0 w-5 h-5 mr-2 flex items-center justify-center hover:bg-gray-200 rounded"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded(node.actorId);
              }}
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-600" />
              )}
            </button>
          ) : (
            <div className="w-5 h-5 mr-2" />
          )}

          <div className="flex-shrink-0 w-4 h-4 mr-2 flex items-center justify-center">
            {hasChildren ? (
              <img src="/assets/location.png" alt="" className="w-4 h-4" />
            ) : (
              <img src="/assets/electricity.png" alt="" className="w-4 h-4" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <span className={cn(
                "text-sm truncate",
                isSelected ? "text-blue-900 font-medium" : "text-gray-900",
                (isTopLevelParent || isSubGroup) && "font-medium"
              )}>
                {highlightMatch(node.name || `Actor ${node.actorId}`)}
              </span>
            </div>

          </div>
        </div>

        {hasChildren && isExpanded && (
          <div>
            {node.children.map((child, childIndex) => renderNode(child, childIndex))}
          </div>
        )}
      </div>
    );
  };

  if (treeData.length === 0) {
    return (
      <div className={cn("p-8 text-center", className)}>
        <Building className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <p className="text-gray-500">No actor tree data available</p>
        <p className="text-sm text-gray-400 mt-2">
          Please select an operator to view the hierarchy.
        </p>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col h-full", className)}>
      <div className="flex items-center gap-2 mb-2">
        <div className="flex-1">
          <TreeSearchBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            matchCount={matchCount}
          />
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={isAllExpanded ? handleCollapseAll : handleExpandAll}
          className="h-8 w-8 p-0"
          title={isAllExpanded ? "Collapse all" : "Expand all"}
        >
          {isAllExpanded ? (
            <ChevronsDownUp className="h-4 w-4" />
          ) : (
            <ChevronsUpDown className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Hide Empty Locations Filter */}
      <div className="flex items-center gap-2 mb-2 px-1">
        <Checkbox
          id="hide-empty-locations"
          checked={hideEmptyLocations}
          onCheckedChange={(checked) => setHideEmptyLocations(checked as boolean)}
        />
        <label
          htmlFor="hide-empty-locations"
          className="text-sm text-gray-700 cursor-pointer select-none"
        >
          Hide Empty Locations
        </label>
      </div>

      <div 
        ref={scrollContainerRef}
        className="flex-1 border border-gray-200 rounded-lg actor-tree-scroll"
        style={{ 
          height: '100%',
          overflowY: 'scroll',
          overflowX: 'hidden',
        }}
      >
        <div className="p-2">
          {filteredTreeData.length > 0 ? (
            filteredTreeData.map((node, index) => renderNode(node, index))
          ) : (
            <div className="p-8 text-center">
              <Search className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-500">No matches found</p>
              <p className="text-sm text-gray-400 mt-2">
                Try searching with a different term
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
